<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type { UseHardalEvents } from '../../composables/useHardalEvents'
import { eventLabel } from '../../service/keyEvents'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, MultiSelect, PlatformLogo, Select, Spinner } from '@/shared/ui'

const props = defineProps<{ controller: UseHardalEvents }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()
const open = ref(true)
const selected = ref([...(props.controller.events.value.value ?? [])])
const primary = ref<string | null>(props.controller.primary.value.value)
const options = computed(() =>
  props.controller.catalog.value.map((value) => ({ value, label: eventLabel(value) })),
)
const selectedOptions = computed(() =>
  selected.value.map((value) => ({ value, label: eventLabel(value) })),
)

async function save(): Promise<void> {
  if (await props.controller.save(selected.value, primary.value)) emit('close')
}

watch(selected, (events) => {
  if (primary.value && !events.includes(primary.value)) primary.value = null
})
onMounted(() => void props.controller.load())
</script>

<template>
  <Modal v-model="open" size="md" @close="emit('close')">
    <template #title>
      <span class="hardal-modal__title inline-flex items-center gap-sm">
        <PlatformLogo platform="hardal" :size="20" />
        {{ t('workspaceDefinitions.conversions.hardal.title') }}
      </span>
    </template>
    <template #body>
      <div class="hardal-modal__body grid gap-lg">
        <div v-if="controller.loading.value" class="hardal-modal__center flex justify-center p-lg">
          <Spinner />
        </div>
        <Alert v-else-if="controller.error.value && !controller.catalog.value.length" tone="danger">
          {{ t('workspaceDefinitions.conversions.hardal.loadError') }}
        </Alert>
        <template v-else>
          <MultiSelect
            v-model="selected"
            :options="options"
            :label="t('workspaceDefinitions.conversions.keyEvents.events')"
            :placeholder="t('workspaceDefinitions.conversions.keyEvents.eventPlaceholder')"
            searchable
            :max="5"
          />
          <Select
            v-if="selected.length"
            v-model="primary"
            :options="selectedOptions"
            :label="t('workspaceDefinitions.conversions.keyEvents.primary')"
            :placeholder="t('workspaceDefinitions.conversions.keyEvents.conversionPlaceholder')"
            searchable
          />
        </template>
        <Alert v-if="controller.error.value && controller.catalog.value.length" tone="danger">
          {{ t('workspaceDefinitions.common.saveError') }}
        </Alert>
      </div>
    </template>
    <template #buttons>
      <Button
        variant="outline"
        size="sm"
        :disabled="controller.pending.value"
        @click="emit('close')"
      >
        {{ t('workspaceDefinitions.common.cancel') }}
      </Button>
      <Button
        size="sm"
        :loading="controller.pending.value"
        :disabled="!selected.length || !primary"
        data-testid="hardal-events-save"
        @click="save"
      >
        {{ t('workspaceDefinitions.common.save') }}
      </Button>
    </template>
  </Modal>
</template>
